import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '@/lib/axiosClient';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/store/auth';
import type { InsertUser, User as SelectUser } from '@shared/types/schema';
import { useEffect } from 'react';
import { getErrorMessage } from '@/lib/error.utils';

type LoginData = Pick<InsertUser, 'email' | 'password'>;
type AuthResponse = { token: string; user: SelectUser };

export function useAuth() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const token = useAuthStore((s) => s.token);
  const setToken = useAuthStore((s) => s.setToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const { data, isLoading, error, isError } = useQuery<SelectUser | null, Error>({
    queryKey: ['user'],
    queryFn: () => axiosClient.get('/api/user').then((res) => res.data),
    retry: 1,
    enabled: !!token,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (isError && error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        clearAuth();
        queryClient.removeQueries({
          queryKey: ['user'],
          exact: true,
        });
      }
    }
  }, [isError, error]);

  const loginMutation = useMutation<AuthResponse, Error, LoginData>({
    mutationFn: (creds) => axiosClient.post('/api/auth/login', creds).then((r) => r.data),
    onSuccess: ({ token: t, user }) => {
      setToken(t);
      queryClient.setQueryData(['user'], user);
    },
    onError: (err) => {
      toast({ title: 'Ошибка входа', description: getErrorMessage(err), variant: 'destructive' });
    },
  });

  const logoutMutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      await axiosClient.post('/api/auth/logout');
      clearAuth();
    },
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['user'],
        exact: true,
      });
      queryClient.clear();
    },
    onError: (err) => {
      toast({ title: 'Ошибка выхода', description: getErrorMessage(err), variant: 'destructive' });
    },
  });

  const registerMutation = useMutation<AuthResponse, Error, InsertUser>({
    mutationFn: (data) => axiosClient.post('/api/auth/register', data).then((r) => r.data),
    onSuccess: ({ token: t, user }) => {
      setToken(t);
      queryClient.setQueryData(['user'], user);
    },
    onError: (err) => {
      toast({
        title: 'Ошибка регистрации',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    },
  });

  const passwordResetMutation = useMutation<{ message: string }, Error, string>({
    mutationFn: (email) => axiosClient.post('/api/reset-password', { email }).then((r) => r.data),
    onError: (err) => {
      toast({
        title: 'Ошибка сброса пароля',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
    },
    onSuccess: (data) => {
      toast({ title: 'Пароль сброшен', description: data.message });
    },
  });

  return {
    user: data,
    token,
    isLoading: isLoading,
    error: error,
    login: loginMutation,
    logout: logoutMutation,
    register: registerMutation,
    passwordReset: passwordResetMutation,
  };
}
